import { useCallback, useState } from 'react';
import { Control, Path, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useAPI, ContractorShortView } from '@nexelem/api';
import { FormSelectInput } from '@nexelem/form-components';

type FormClientSelectInputProps<T extends { buyer: ContractorShortView }> = {
  buyerId?: number;
  required?: boolean;
  control?: Control<T>;
  name: Path<T>;
};

export const FormClientSelectInput = <T extends { buyer: ContractorShortView }>({
  buyerId,
  required,
  control,
  name,
}: FormClientSelectInputProps<T>) => {
  const api = useAPI();
  const { field } = useController({ name, control });

  const [isInputOpen, setIsInputOpen] = useState(false);
  const [inputText, setInputText] = useState('');

  const { t } = useTranslation();

  const { fetchNextPage, data } = useInfiniteQuery({
    queryKey: ['contractors', inputText],
    queryFn: ({ pageParam = 0 }) =>
      api.postBackContractorsListFilter({
        postBackContractorsListFilterRequest: {
          data: {
            comboNameSearch: inputText,
            active: true,
          },
          take: (pageParam + 1) * 10,
          skip: pageParam * 10,
        },
      }),
    select: (response) => response.pages.flatMap((page) => page.result.data || []),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      allPages.reduce((sum, page) => sum + (page.result.data?.length ?? 0), 0) < lastPage.result.total
        ? allPages.length
        : undefined,
    enabled: isInputOpen,
  });

  useQuery({
    queryKey: ['contractors', buyerId],
    queryFn: async () => {
      const response = await api.getBackContractorsP1({ p1: buyerId || 0 });
      field.onChange(response);
    },
    enabled: !!buyerId,
  });

  const onToggle = useCallback(() => setIsInputOpen((prevState) => !prevState), []);

  return (
    <div>
      <span className="text-t16-sb">{t('components.clientField.name')}</span>
      <FormSelectInput
        className="mt-4"
        control={control}
        filterOptions={(option) => option}
        getOptionLabel={(option) => option.name}
        inputValue={inputText}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        label={t('components.clientField.name')}
        name={name}
        open={isInputOpen}
        options={data ?? []}
        placeholder={t('forms.placeholders.searchOrChoose')}
        required={required}
        onClose={onToggle}
        onInputChange={setInputText}
        onOpen={onToggle}
        onScrollEnd={fetchNextPage}
      />
    </div>
  );
};
